import React, { useEffect, useState } from "react";
import { Modal } from "../../../components/Modal";
import { Input } from "../../../components/Input";
import { theme } from "../../../styles/theme";
import { Div, Text } from "../../../styles/Common";
import { Alert, Col, Row, Tooltip } from "antd";
import { Button } from "../../../components/Button";
import infoIcon from "../../../assets/icons/info.svg";
import useUpdateUser from "../hooks/useUpdateUser";
import { SelectMultiple } from "../../../components/SelectMultiple";
import { Select } from "../../../components/Select";
import { getPhoneLengthByCountry } from "../../../utilities/helpers";
import { validateEditUser } from "../../../utilities/validations";

export const EditUserModal = ({
  openCreate,
  handleClose,
  userInfo,
  setUserInfo,
  refetch,
  contractorCompaniesOptions,
  codeOptions,
}) => {
  const [errorMessage, setErrorMessage] = useState("");
  const [userErrorForm, setUserErrorForm] = useState({
    name: { error: false, message: "" },
    lastName: { error: false, message: "" },
    email: { error: false, message: "" },
    phoneCodeId: { error: false, message: "" },
    phoneNumber: { error: false, message: "" },
    companiesContractorIds: { error: false, message: "" },
  });
  const [forceUpdate, setForceUpdate] = useState(false);
  const updateUser = useUpdateUser();

  const handleCancel = () => {
    handleClose();
    setErrorMessage("");
    setUserErrorForm({
      name: { error: false, message: "" },
      lastName: { error: false, message: "" },
      email: { error: false, message: "" },
      phoneCodeId: { error: false, message: "" },
      phoneNumber: { error: false, message: "" },
      companiesContractorIds: { error: false, message: "" },
    });
  };

  const handleChange = (event) => {
    const { id, value } = event.target;
    const newUserInfo = { ...userInfo };
    newUserInfo[id] = value;
    setUserInfo(newUserInfo);
    
    const newErrorForm = { ...userErrorForm };
    newErrorForm[id].error = false;
    newErrorForm[id].message = "";
    setUserErrorForm(newErrorForm);
    setForceUpdate(!forceUpdate);
  };

  const handleSelectCompany = (value) => {
    const newUserInfo = { ...userInfo };
    newUserInfo.companiesContractorIds = value;
    setUserInfo(newUserInfo);
    
    const newErrorForm = { ...userErrorForm };
    newErrorForm.companiesContractorIds.error = false;
    newErrorForm.companiesContractorIds.message = "";
    setUserErrorForm(newErrorForm);
    setForceUpdate(!forceUpdate);
  };

  const handleSelectPhoneCode = (value) => {
    const newUserInfo = { ...userInfo };
    newUserInfo.phoneCodeId = value;
    setUserInfo(newUserInfo);
    
    const newErrorForm = { ...userErrorForm };
    newErrorForm.phoneCodeId.error = false;
    newErrorForm.phoneCodeId.message = "";
    setUserErrorForm(newErrorForm);
    setForceUpdate(!forceUpdate);
  };

  const renderTitle = () => {
    return (
      <Div direction="column" gap="16px" style={{ width: "350px" }}>
        <Div align="center" gap="8px">
          <img alt="info" src={infoIcon} />
          <Text color={theme.colors.dark} weight={theme.fonts.weight.medium}>
            Changing the email will require re-invitation
          </Text>
        </Div>
        <Text color={theme.colors.grey}>
          The user must accept a new invitation sent to the updated email address to regain access.
        </Text>
      </Div>
    )
  };

  const renderInfoIcon = () => {
    return (
      <Tooltip
        title={renderTitle()}
        color={theme.colors.white}
        overlayStyle={{ width: 350, minWidth: 350 }}
      >
        <img alt="info" src={infoIcon} />
      </Tooltip>
    )
  };

  const handleUpdateUser = () => {
    setErrorMessage("");
    
    const validation = validateEditUser.validate(userInfo, {
      abortEarly: false,
    });

    if (validation?.error) {
      const newErrorForm = { ...userErrorForm };
      validation?.error.details.forEach((ele) => {
        if (newErrorForm[ele.context.label]) {
          newErrorForm[ele.context.label].error = true;
          newErrorForm[ele.context.label].message =
            "Required field or invalid format";
        }
      });
      setUserErrorForm(newErrorForm);
      setForceUpdate(!forceUpdate);
    } else {
      updateUser.reset();
      updateUser.mutate(userInfo, {
        onSuccess: () => {
          handleCancel();
          refetch();
        },
        onError: (err) => {
          setErrorMessage(err.response.data?.Errors[0]);
        },
      });
    }
  };

  const countryCode = codeOptions?.find(option => option.value === userInfo?.phoneCodeId)?.label;
  const maxLength = getPhoneLengthByCountry(countryCode);

  return (
    <Modal
      open={openCreate}
      onCancel={handleCancel}
      align={"left"}
      padding={"41px 39px"}
      width={"428px"}
    >
      <Modal.Header 
        padding="16px 0 16px 0" 
        title={"Edit User"} 
        align={"flex-start"} 
        subtitle={"Update the user's details or change their assigned role. Changes will take effect immediately."} 
      />
      <Modal.Body
        margin="30px 0 42px 0"
        style={{ paddingRight: "24px" }}
      >
        <Div direction="column" gap="24px">
          <Row gutter={[24, 24]}>
            <Col md={24}>
              <Input 
                id="name" 
                placeholder="Name" 
                value={userInfo?.name} 
                onChange={handleChange}
                error={userErrorForm?.name?.error}
                helper={userErrorForm?.name?.error ? userErrorForm?.name?.message : ""}
              />
            </Col>
            <Col md={24}>
              <Input 
                id="lastName" 
                placeholder="Lastname" 
                value={userInfo?.lastName} 
                onChange={handleChange}
                error={userErrorForm?.lastName?.error}
                helper={userErrorForm?.lastName?.error ? userErrorForm?.lastName?.message : ""}
              />
            </Col>
            <Col md={24}>
              <Input 
                id="email" 
                placeholder="Email Address" 
                value={userInfo?.email} 
                onChange={handleChange} 
                suffix={renderInfoIcon()}
                error={userErrorForm?.email?.error}
                helper={userErrorForm?.email?.error ? userErrorForm?.email?.message : ""}
              /> 
            </Col>
            <Col md={8}>
              <Select
                id="phoneCodeId"
                options={codeOptions}
                placeholder="Code"
                value={userInfo?.phoneCodeId}
                onChange={handleSelectPhoneCode}
                error={userErrorForm?.phoneCodeId?.error}
                helper={userErrorForm?.phoneCodeId?.error ? userErrorForm?.phoneCodeId?.message : ""}
              />
            </Col>
            <Col md={16}>
              <Input 
                id="phoneNumber" 
                placeholder="Phone Number" 
                onChange={handleChange} 
                value={userInfo?.phoneNumber}
                maxLength={maxLength}
                error={userErrorForm?.phoneNumber?.error}
                helper={userErrorForm?.phoneNumber?.error ? userErrorForm?.phoneNumber?.message : ""}
              /> 
            </Col>
            <Col md={24}>
              <SelectMultiple
                options={contractorCompaniesOptions}
                placeholder="Select Company"
                autoComplete="off"
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
                notFoundContent="Data not found"
                showSearch
                value={userInfo?.companiesContractorIds}
                onChange={handleSelectCompany}
                error={userErrorForm?.companiesContractorIds?.error}
                helper={userErrorForm?.companiesContractorIds?.error ? userErrorForm?.companiesContractorIds?.message : ""}
              />
            </Col>
          </Row>
        </Div>
      </Modal.Body>
      <Modal.Footer style={{ flexDirection: "column" }}>
        {errorMessage !== "" && (
          <Div m="0 0 20px 0" justify="flex-end">
            <Alert closable message={`${errorMessage}`} type="error" />
          </Div>
        )}
        <Div gap="32px" justify="center">
          <Button
            onClick={handleCancel}
            border={theme.colors.dark}
            color={theme.colors.dark}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateUser}
            background={theme.colors.salmon}
            loading={updateUser.isLoading}
          >
            Edit User
          </Button>
        </Div>
      </Modal.Footer>
    </Modal>
  )
} 